import { useState, useRef } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

export default function SiteImageUploader({ onClose }: { onClose: () => void }) {
  const sitesWithoutImages = useQuery(api.sites.getSitesWithoutImages);
  const allSites = useQuery(api.sites.getAll);
  const generateUploadUrl = useMutation(api.sites.generateUploadUrl);
  const saveSiteImage = useMutation(api.sites.saveSiteImage);
  
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const createThumbnailBlob = async (file: Blob) => {
    const maxDim = 640;
    const quality = 0.78;

    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('SITE-UPLOAD-THUMB-001 Canvas ctx missing');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close?.();
      const out = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
      if (!out) throw new Error('SITE-UPLOAD-THUMB-002 Failed to encode thumbnail');
      return out;
    }

    const img = new Image();
    img.decoding = 'async';
    const url = URL.createObjectURL(file);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('SITE-UPLOAD-THUMB-003 Image decode failed'));
      img.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('SITE-UPLOAD-THUMB-004 Canvas ctx missing');
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    const out = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality));
    if (!out) throw new Error('SITE-UPLOAD-THUMB-005 Failed to encode thumbnail');
    return out;
  };

  const uploadToConvexStorage = async (uploadUrl: string, file: Blob, contentType: string) => {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!response.ok) throw new Error(`SITE-UPLOAD-001 Upload failed: ${response.status}`);
    const { storageId } = await response.json();
    if (!storageId) throw new Error('SITE-UPLOAD-002 Missing storageId');
    return storageId as Id<'_storage'>;
  };

  const filteredSites = allSites?.filter((site: any) => 
    site.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleSingleUpload = async (file: File, siteId: string) => {
    try {
      // Generate thumbnail client-side (fast sidebar rendering)
      const thumbBlob = await createThumbnailBlob(file);

      // Get upload URLs from Convex
      const [fullUploadUrl, thumbUploadUrl] = await Promise.all([generateUploadUrl(), generateUploadUrl()]);

      // Upload full + thumb
      const [storageId, thumbStorageId] = await Promise.all([
        uploadToConvexStorage(fullUploadUrl, file, file.type || 'application/octet-stream'),
        uploadToConvexStorage(thumbUploadUrl, thumbBlob, 'image/jpeg'),
      ]);

      // Save the storage IDs to the site
      await saveSiteImage({
        siteId: siteId as Id<"sites">,
        storageId,
        thumbStorageId,
      });
      
      return true;
    } catch (error) {
      console.error('Upload error:', error);
      return false;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSiteId) return;
    
    setUploading(true);
    setUploadProgress('Uploading...');
    
    const success = await handleSingleUpload(file, selectedSiteId);
    
    setUploadProgress(success ? 'Upload complete!' : 'Upload failed');
    setUploading(false);
    setSelectedSiteId('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let uploaded = 0;
    let failed = 0;
    
    for (const file of Array.from(files)) {
      // Extract site name from filename (remove extension)
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      
      setUploadProgress(`Uploading ${uploaded + failed + 1}/${files.length}: ${fileName}`);
      
      // Find matching site
      const site = allSites?.find((s: any) => {
        const normalizedSiteName = s.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        const normalizedFileName = fileName.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalizedSiteName === normalizedFileName;
      });
      
      if (site) {
        const success = await handleSingleUpload(file, site._id);
        if (success) {
          uploaded++;
        } else {
          failed++;
        }
      } else {
        console.log(`No matching site for: ${fileName}`);
        failed++;
      }
    }
    
    setUploadProgress(`Complete! ${uploaded} uploaded, ${failed} failed`);
    setUploading(false);
    
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="uploader-modal" onClick={e => e.stopPropagation()}>
        <div className="uploader-header">
          <h2>Upload Site Images</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="uploader-content">
          {/* Bulk Upload Section */}
          <div className="upload-section">
            <h3>Bulk Upload</h3>
            <p className="upload-hint">
              Select multiple images. Filenames should match site names with underscores 
              (e.g., "Tierra_Plaza.png" for "Tierra Plaza")
            </p>
            <input
              ref={folderInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleBulkUpload}
              disabled={uploading}
              className="file-input"
            />
          </div>
          
          <div className="upload-divider">OR</div>
          
          {/* Single Upload Section */}
          <div className="upload-section">
            <h3>Single Upload</h3>
            <input
              type="text"
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            
            <div className="sites-select-list">
              {filteredSites.slice(0, 10).map((site: any) => (
                <div 
                  key={site._id}
                  className={`site-select-item ${selectedSiteId === site._id ? 'selected' : ''} ${site.imageId ? 'has-image' : ''}`}
                  onClick={() => setSelectedSiteId(site._id)}
                >
                  <span className="site-select-name">{site.name}</span>
                  {site.imageId && <span className="has-image-badge">✓</span>}
                </div>
              ))}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading || !selectedSiteId}
              className="file-input"
            />
          </div>
          
          {/* Progress */}
          {uploadProgress && (
            <div className="upload-progress">
              {uploading && <div className="spinner"></div>}
              <span>{uploadProgress}</span>
            </div>
          )}
          
          {/* Stats */}
          <div className="upload-stats">
            <span>Sites without images: {sitesWithoutImages?.length || 0}</span>
            <span>Total sites: {allSites?.length || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
